import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Typography,
  Row,
  Col,
  Table,
  Space,
  Modal,
  InputNumber,
  Divider,
  Select,
  Switch,
  Tooltip,
  message,
  Drawer,
  Tag,
  Avatar,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  SwapOutlined,
  BankOutlined,
  CheckCircleOutlined,
  UserOutlined,
  TeamOutlined,
  StopOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import { AppContext } from "../context/AppContext";
import AccountsService from "../utils/accountsService";
import axios from "axios";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

// Zambian colors
const ZAMBIAN_COLORS = {
  green: "#078930",
  red: "#EF3340",
  black: "#000000",
  orange: "#FFB81C",
};

const AccountsManagement = () => {
  const { company, updateAccounts } = useContext(AppContext);
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [accountForm] = Form.useForm();
  const [transferForm] = Form.useForm();
  const [isAccountModalVisible, setIsAccountModalVisible] = useState(false);
  const [isTransferModalVisible, setIsTransferModalVisible] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchAccounts();
    fetchUsers();
  }, [company]);

  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      if (company?.id) {
        const response = await axios.get(
          `https://zedlenders.pickmesms.com/users?fields=*.*&filter[role][name][_neq]=Borrower&filter[company][_eq]=${company.id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setUsers(response.data.data);
      }
    } catch (error) {
      message.error("Failed to fetch users");
      console.error(error);
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      if (company?.id) {
        // Update AccountsService call to include the users field
        const response = await axios.get(
          `https://zedlenders.pickmesms.com/items/accounts?filter[company]=${company.id}&fields=*,users.*.*`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        console.log(response.data.data);
        setAccounts(response.data.data);
        // Also update the context so the navbar gets refreshed
        updateAccounts(response.data.data);
      }
    } catch (error) {
      message.error("Failed to fetch accounts");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrUpdate = async (values) => {
    try {
      if (editingAccount) {
        // For updates, handle users associations differently
        const userAssignments = (values.users || []).map((userId) => ({
          directus_users_id: userId,
        }));

        const accountData = {
          ...values,
          company: company.id,
          users: userAssignments,
        };

        await AccountsService.updateAccount(
          editingAccount.id,
          accountData,
          token
        );
        message.success("Account updated successfully");
      } else {
        // For creation, create the account first without users
        const { users: selectedUsers, ...accountDataWithoutUsers } = values;
        
        const accountData = {
          ...accountDataWithoutUsers,
          company: company.id,
          status: "active",
        };

        console.log("Creating account with data:", accountData);
        const createdAccount = await AccountsService.createAccount(accountData, token);
        
        // If users are selected, create the junction records separately
        if (selectedUsers && selectedUsers.length > 0) {
          const userPromises = selectedUsers.map(userId => 
            axios.post(
              'https://zedlenders.pickmesms.com/items/accounts_directus_users',
              {
                accounts_id: createdAccount.id,
                directus_users_id: userId
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              }
            )
          );
          
          await Promise.all(userPromises);
        }
        
        message.success("Account created successfully");
      }

      setIsAccountModalVisible(false);
      accountForm.resetFields();
      setEditingAccount(null);
      fetchAccounts();
    } catch (error) {
      message.error("Operation failed. Please try again.");
      console.error("Account operation error:", error);
      
      // Log more details about the error
      if (error.response) {
        console.error("Error response:", error.response.data);
        console.error("Error status:", error.response.status);
      }
    }
  };

  const handleTransfer = async (values) => {
    try {
      const { from_account, to_account, amount, notes } = values;

      if (from_account === to_account) {
        message.error("Source and destination accounts must be different");
        return;
      }

      const sourceAccount = accounts.find((acc) => acc.id === from_account);

      if (!sourceAccount || sourceAccount.status !== "active") {
        message.error("Source account is not active");
        return;
      }

      if (sourceAccount.balance < amount) {
        message.error("Insufficient balance in source account");
        return;
      }

      const destinationAccount = accounts.find((acc) => acc.id === to_account);
      if (!destinationAccount || destinationAccount.status !== "active") {
        message.error("Destination account is not active");
        return;
      }

      await AccountsService.transferFloat(
        from_account,
        to_account,
        amount,
        token,
        notes
      );

      message.success("Funds transferred successfully");
      setIsTransferModalVisible(false);
      transferForm.resetFields();
      fetchAccounts();
    } catch (error) {
      message.error("Transfer failed. Please try again.");
      console.error(error);
    }
  };

  const handleDeactivateAccount = async (accountId) => {
    try {
      const account = accounts.find(acc => acc.id === accountId);
      if (account?.is_default) {
        message.error("Cannot deactivate default account");
        return;
      }

      await axios.patch(
        `https://zedlenders.pickmesms.com/items/accounts/${accountId}`,
        { status: "inactive" },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      message.success("Account deactivated successfully");
      fetchAccounts();
    } catch (error) {
      message.error("Failed to deactivate account");
      console.error(error);
    }
  };

  const handleActivateAccount = async (accountId) => {
    try {
      await axios.patch(
        `https://zedlenders.pickmesms.com/items/accounts/${accountId}`,
        { status: "active" },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      message.success("Account activated successfully");
      fetchAccounts();
    } catch (error) {
      message.error("Failed to activate account");
      console.error(error);
    }
  };

  const showEditModal = (account) => {
    setEditingAccount(account);

    // Extract user IDs from the account's users array
    const userIds =
      account.users && Array.isArray(account.users)
        ? account.users.map((user) => user.directus_users_id.id)
        : [];

    accountForm.setFieldsValue({
      name: account.name,
      account_code: account.account_code,
      description: account.description,
      is_default: account.is_default,
      balance: account.balance,
      users: userIds,
    });
    setIsAccountModalVisible(true);
  };

  const columns = [
    {
      title: "Account Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <Space>
          {text}
          {record.is_default && (
            <Tooltip title="Default Account">
              <CheckCircleOutlined style={{ color: ZAMBIAN_COLORS.green }} />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "Account Code",
      dataIndex: "account_code",
      key: "account_code",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      render: (balance) => (
        <Text strong>
          K{" "}
          {parseFloat(balance)
            .toFixed(2)
            .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
        </Text>
      ),
      sorter: (a, b) => a.balance - b.balance,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const actualStatus = status || "active";
        return (
          <Tag color={actualStatus === "active" ? ZAMBIAN_COLORS.green : ZAMBIAN_COLORS.red}>
            {actualStatus === "active" ? "Active" : "Inactive"}
          </Tag>
        );
      },
    },
    {
      title: "Assigned Users",
      dataIndex: "users",
      key: "users",
      render: (users) => (
        <Space size="small" wrap>
          {users && Array.isArray(users) && users.length > 0 ? (
            users.map((user) => (
              <Tag key={user.id} color={ZAMBIAN_COLORS.green}>
                <Space size="small">
                  <Avatar
                    size="small"
                    icon={<UserOutlined />}
                    style={{ backgroundColor: ZAMBIAN_COLORS.orange }}
                  />
                  {user.directus_users_id.first_name}
                </Space>
              </Tag>
            ))
          ) : (
            <Text type="secondary">No users assigned</Text>
          )}
        </Space>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => showEditModal(record)}
            style={{
              backgroundColor: ZAMBIAN_COLORS.green,
              borderColor: ZAMBIAN_COLORS.green,
            }}
          >
            Edit
          </Button>
          {(record.status || "active") === "active" ? (
            <Button
              danger
              icon={<StopOutlined />}
              onClick={() => handleDeactivateAccount(record.id)}
              disabled={record.is_default}
              title={record.is_default ? "Cannot deactivate default account" : ""}
              size="small"
            >
              Deactivate
            </Button>
          ) : (
            <Button
              type="primary"
              icon={<CheckOutlined />}
              onClick={() => handleActivateAccount(record.id)}
              size="small"
              style={{
                backgroundColor: ZAMBIAN_COLORS.green,
                borderColor: ZAMBIAN_COLORS.green,
              }}
            >
              Activate
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        bordered={false}
        style={{
          borderTop: `4px solid ${ZAMBIAN_COLORS.green}`,
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.09)",
        }}
      >
        <Row
          align="middle"
          justify="space-between"
          style={{ marginBottom: 24 }}
        >
          <Col>
            <Title
              level={3}
              style={{ color: ZAMBIAN_COLORS.green, marginBottom: 0 }}
            >
              Accounts Management
            </Title>
            <Text type="secondary">
              Manage company accounts and transfer funds between them
            </Text>
          </Col>
          <Col>
            <Space>
              <Button
                type="primary"
                icon={<SwapOutlined />}
                onClick={() => setIsTransferModalVisible(true)}
                style={{
                  backgroundColor: ZAMBIAN_COLORS.orange,
                  borderColor: ZAMBIAN_COLORS.orange,
                }}
              >
                Transfer Funds
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingAccount(null);
                  accountForm.resetFields();
                  setIsAccountModalVisible(true);
                }}
                style={{
                  backgroundColor: ZAMBIAN_COLORS.green,
                  borderColor: ZAMBIAN_COLORS.green,
                }}
              >
                Add Account
              </Button>
            </Space>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={accounts}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          style={{ marginTop: 16 }}
          scroll={{ x: 'max-content' }}
        />

        {/* Account Form Modal */}
        <Modal
          title={
            <div style={{ color: ZAMBIAN_COLORS.green }}>
              {editingAccount ? "Edit Account" : "Create New Account"}
            </div>
          }
          open={isAccountModalVisible}
          footer={null}
          onCancel={() => {
            setIsAccountModalVisible(false);
            setEditingAccount(null);
          }}
          destroyOnClose
        >
          <Form
            form={accountForm}
            layout="vertical"
            onFinish={handleCreateOrUpdate}
          >
            <Form.Item
              name="name"
              label="Account Name"
              rules={[{ required: true, message: "Please enter account name" }]}
            >
              <Input placeholder="e.g. Main Account, Operations Account, etc." />
            </Form.Item>

            <Form.Item
              name="account_code"
              label="Account Code"
              rules={[{ required: true, message: "Please enter account code" }]}
            >
              <Input placeholder="e.g. MAIN-001, OPS-002, etc." />
            </Form.Item>

            <Form.Item name="description" label="Description">
              <Input.TextArea placeholder="Account purpose and details..." />
            </Form.Item>

            <Form.Item
              name="is_default"
              label="Default Account"
              valuePropName="checked"
              extra="Set as the company's default account"
            >
              <Switch />
            </Form.Item>

            <Form.Item
              name="users"
              label={
                <span>
                  <TeamOutlined /> Assigned Users
                </span>
              }
              help="Select users who can access this account"
            >
              <Select
                mode="multiple"
                placeholder="Select users to assign"
                loading={usersLoading}
                optionFilterProp="children"
                style={{ width: "100%" }}
                showSearch
                filterOption={(input, option) =>
                  option.children?.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
                optionLabelProp="label"
              >
                {users.map((user) => (
                  <Option
                    key={user.id}
                    value={user.id}
                    label={`${user.first_name} ${user.last_name}`}
                  >
                    <Space>
                      <Avatar
                        size="small"
                        icon={<UserOutlined />}
                        style={{ backgroundColor: ZAMBIAN_COLORS.orange }}
                      />
                      {user.first_name} {user.last_name} (
                      {user.role?.name || "User"})
                    </Space>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="balance"
              label={editingAccount ? "Balance (Manual Override)" : "Initial Balance"}
              rules={[
                {
                  required: true,
                  message: editingAccount
                    ? "Please enter account balance"
                    : "Please enter initial balance",
                },
              ]}
              extra={
                editingAccount
                  ? "Use this to correct the displayed balance in the navbar."
                  : null
              }
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder={
                  editingAccount ? "Enter current balance" : "Enter initial balance"
                }
                min={0}
                step={100}
                formatter={(value) =>
                  `K ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) => value.replace(/K\s?|(,*)/g, "")}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
              <Space>
                <Button
                  onClick={() => {
                    setIsAccountModalVisible(false);
                    setEditingAccount(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  style={{
                    backgroundColor: ZAMBIAN_COLORS.green,
                    borderColor: ZAMBIAN_COLORS.green,
                  }}
                >
                  {editingAccount ? "Update" : "Create"}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Fund Transfer Modal */}
        <Modal
          title={
            <div style={{ color: ZAMBIAN_COLORS.orange }}>
              <SwapOutlined /> Transfer Funds Between Accounts
            </div>
          }
          open={isTransferModalVisible}
          footer={null}
          onCancel={() => setIsTransferModalVisible(false)}
          destroyOnClose
        >
          <Form form={transferForm} layout="vertical" onFinish={handleTransfer}>
            <Form.Item
              name="from_account"
              label="From Account"
              rules={[
                { required: true, message: "Please select source account" },
              ]}
            >
              <Select placeholder="Select source account">
                {accounts.filter(account => account.status === "active").map((account) => (
                  <Option key={account.id} value={account.id}>
                    {account.name} (Balance: K
                    {parseFloat(account.balance).toFixed(2)})
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="to_account"
              label="To Account"
              rules={[
                {
                  required: true,
                  message: "Please select destination account",
                },
              ]}
            >
              <Select placeholder="Select destination account">
                {accounts.filter(account => account.status === "active").map((account) => (
                  <Option key={account.id} value={account.id}>
                    {account.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="amount"
              label="Amount to Transfer"
              rules={[
                { required: true, message: "Please enter amount" },
                {
                  type: "number",
                  min: 1,
                  message: "Amount must be at least K1",
                },
              ]}
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="Enter amount to transfer"
                min={1}
                step={100}
                formatter={(value) =>
                  `K ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) => value.replace(/K\s?|(,*)/g, "")}
              />
            </Form.Item>

            <Form.Item name="notes" label="Notes">
              <Input.TextArea placeholder="Add notes about this transfer..." />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
              <Space>
                <Button onClick={() => setIsTransferModalVisible(false)}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  style={{
                    backgroundColor: ZAMBIAN_COLORS.orange,
                    borderColor: ZAMBIAN_COLORS.orange,
                  }}
                >
                  Transfer Funds
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </div>
  );
};

export default AccountsManagement;
